import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import SharedShareOnSocials from '@eneco/components/ShareOnSocials/ShareOnSocials';

import type { ShareOnSocialsContent } from './share-on-socials-content.types';

/**
 * `ShareOnSocials` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/content/src/ShareOnSocials/ShareOnSocials.tsx`, which is itself a
 * one-line bridge onto `@eneco/components/ShareOnSocials/ShareOnSocials` — the shared component owns
 * all of the markup (the label, then the LinkedIn/Twitter/Facebook buttons). Passing `label` through
 * is the entire legacy behaviour, so the two lanes render byte-identical output by construction.
 *
 * The shared component reaches for `@eneco/i18n` internally (its own `I18nProvider` supplies the
 * buttons' accessible names from `./content/<locale>.json`). That is a dependency of `lib:components`,
 * which `lib:cms-components` is allowed to depend on — this lane never imports `@eneco/i18n` itself, so
 * the module boundary holds without widening.
 *
 * The legacy component reads `label` off the Sitecore `TextField` wrapper (`label?.value`); on this
 * lane `useContent` has already unwrapped it to a plain string (`libs/cms-sitecore/src/mappers.ts`),
 * and the Sanity schema declares it as a bare `string`, so both arrive the same way.
 */
const ShareOnSocials: FC = () => {
  const content = useContent<ShareOnSocialsContent>();

  return <SharedShareOnSocials label={content?.label} />;
};

export default ShareOnSocials;
